import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  OnboardingDialog,
  OrgNoticeConsentDialog,
  type OnboardingSubmitValues,
  type OrgNoticeInfo,
} from '@ludolio/shared-ui';
import { authApi, getErrorMessage } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';
import { FullPageMessage, FullPageSpinner } from './FullPageMessage';

interface OnboardingInfo {
  firstName: string;
  lastName: string;
  orgName: string | null;
  notice: OrgNoticeInfo | null;
}

export function OnboardingGate() {
  const user = useAuthStore((state) => state.user);
  const setAuth = useAuthStore((state) => state.setAuth);
  const logout = useAuthStore((state) => state.logout);
  const [info, setInfo] = useState<OnboardingInfo | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');

  const needsOnboarding = Boolean(user?.needsOnboarding);

  const loadInfo = useCallback(async () => {
    try {
      setInfo(await authApi.getOnboardingInfo());
      setLoadError('');
    } catch (err) {
      setInfo((current) => current ?? {
        firstName: user?.firstName ?? '',
        lastName: user?.lastName ?? '',
        orgName: null,
        notice: null,
      });
      setLoadError(getErrorMessage(err, 'We could not load your account setup. Please try again.'));
    }
  }, [user?.firstName, user?.lastName]);

  useEffect(() => {
    void loadInfo();
  }, [loadInfo]);

  if (!info || !user) return <FullPageSpinner />;

  const applyUser = (next: Partial<typeof user>) => setAuth({
    ...user,
    ...next,
    needsOnboarding: false,
    needsOrgNoticeConsent: Boolean(next.needsOrgNoticeConsent),
  });

  const submitOnboarding = async (values: OnboardingSubmitValues) => {
    setError('');
    setSubmitting(true);
    try {
      const result = await authApi.completeOnboarding({
        firstName: values.firstName,
        lastName: values.lastName,
        marketingConsent: values.marketingConsent,
        privacyAccepted: true,
        orgNoticeId: values.orgNoticeId,
      });
      applyUser(result.user);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to save your details. Please try again.'));
      await loadInfo();
    } finally {
      setSubmitting(false);
    }
  };

  const submitNotice = async (orgNoticeId: string) => {
    setError('');
    setSubmitting(true);
    try {
      const result = await authApi.acceptOrgNotice(orgNoticeId);
      applyUser({ ...result.user, needsOrgNoticeConsent: false });
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to save your acknowledgement. Please try again.'));
      await loadInfo();
    } finally {
      setSubmitting(false);
    }
  };

  if (!needsOnboarding && !info.notice) {
    return (
      <FullPageMessage title="We could not load your university's notice">
        {loadError && <p className="text-sm text-muted-foreground">{loadError}</p>}
        <div className="flex justify-center gap-3">
          <Button variant="outline" onClick={() => void logout()}>Use a different account</Button>
          <Button onClick={() => void loadInfo()}>Try again</Button>
        </div>
      </FullPageMessage>
    );
  }

  if (!needsOnboarding && info.notice) {
    return (
      <OrgNoticeConsentDialog
        open
        orgName={info.orgName}
        notice={info.notice}
        submitting={submitting}
        error={error}
        loadError={loadError}
        onSubmit={submitNotice}
        onRetry={() => void loadInfo()}
        onUseDifferentAccount={() => void logout()}
      />
    );
  }

  return (
    <OnboardingDialog
      open
      initialFirstName={info.firstName}
      initialLastName={info.lastName}
      orgName={info.orgName}
      notice={info.notice}
      submitting={submitting}
      error={error}
      loadError={loadError}
      onSubmit={submitOnboarding}
      onRetry={() => void loadInfo()}
      onUseDifferentAccount={() => void logout()}
    />
  );
}
